import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { UserProfile } from '@clerk/clerk-react';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { isClerkAuth } from '../../config/auth';
import GenericProfilePanel from './GenericProfilePanel';

/**
 * Profile modal -- renders Clerk's UserProfile when AUTH_PROVIDER=clerk,
 * otherwise falls back to the generic profile panel.
 */

const ProfileModal: React.FC = () => {
  const { isProfileModalOpen, closeProfileModal } = useSettingsModal();

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isProfileModalOpen) {
        closeProfileModal();
      }
    };

    if (isProfileModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isProfileModalOpen, closeProfileModal]);

  if (!isProfileModalOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={closeProfileModal}
    >
      <div
        className="relative w-full h-full lg:w-auto lg:h-auto max-w-[95vw] max-h-[95vh] overflow-auto bg-ui-background-secondary rounded-xl shadow-2xl border border-ui-border-primary"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeProfileModal}
          aria-label="Close"
          className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-white/90 dark:bg-neutral-800/90 hover:bg-ui-background-tertiary text-ui-text-tertiary hover:text-ui-text-primary shadow-sm border border-ui-border-primary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        {isClerkAuth ? (
          <UserProfile
            appearance={{
              elements: {
                rootBox: 'w-full h-full',
                card: 'bg-ui-background-secondary shadow-2xl border border-ui-border-primary ring-1 ring-black/5 dark:ring-white/10 rounded-xl',
                profileSection__danger: 'hidden',
              },
            }}
          />
        ) : (
          <GenericProfilePanel />
        )}
      </div>
    </div>
  );
};

export default ProfileModal;
